import { useState, useMemo, Component, type ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/app/components/button";

/** Catches React errors (e.g. #300 Objects are not valid as React child) so the app doesn’t crash. */
class StripeFormErrorBoundary extends Component<
  { children: ReactNode; onBack: () => void; fallbackMessage?: string },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError = () => ({ hasError: true });
  componentDidCatch() {
    this.setState({ hasError: true });
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-6 text-center">
          <p className="text-destructive font-medium mb-2">
            {this.props.fallbackMessage ?? "Algo deu errado ao carregar o formulário de pagamento."}
          </p>
          <Button onClick={this.props.onBack} variant="outline" className="w-full">
            Voltar
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

/** Coerce to string so we never render an object as React child (error #300). */
function safeText(x: unknown): string {
  if (x == null) return "";
  if (typeof x === "string" || typeof x === "number") return String(x);
  return "";
}

const stripePromiseCache: Record<string, Promise<Stripe | null>> = {};

function getStripePromise(publishableKey: string): Promise<Stripe | null> {
  if (!publishableKey) return Promise.resolve(null);
  if (!stripePromiseCache[publishableKey]) {
    stripePromiseCache[publishableKey] = loadStripe(publishableKey);
  }
  return stripePromiseCache[publishableKey];
}

interface CheckoutStripeFormProps {
  orderId: string;
  clientSecret: string;
  onSuccess: (orderId: string) => void;
  onError: (message: string) => void;
}

function CheckoutStripeForm({ orderId, clientSecret, onSuccess, onError }: CheckoutStripeFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const returnUrl = useMemo(() => {
    const base = typeof window !== "undefined" ? window.location.origin + (window.location.pathname || "/") : "";
    return `${base}?stripe_return=1&order_id=${encodeURIComponent(orderId)}`;
  }, [orderId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsSubmitting(true);
    onError("");

    const { error: submitError } = await elements.submit();
    if (submitError) {
      onError(safeText(submitError.message) || "Verifique os dados do pagamento.");
      setIsSubmitting(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (error) {
      onError(safeText(error.message) || "Pagamento não concluído. Tente novamente.");
      setIsSubmitting(false);
      return;
    }

    onSuccess(orderId);
    setIsSubmitting(false);
  };

  const handleLoadError = (event: { elementType: string; error: { message?: string } }) => {
    const msg = event?.error?.message ?? "";
    onError(
      msg.includes("terminal") || msg.includes("cannot be used")
        ? "Este link de pagamento não está mais válido. Volte e tente concluir o pagamento novamente."
        : safeText(msg) || "Não foi possível carregar o formulário de pagamento. Tente voltar e abrir de novo."
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement options={{ layout: "tabs" }} onLoadError={handleLoadError} />
      <Button type="submit" className="w-full" size="lg" disabled={!stripe || isSubmitting}>
        {isSubmitting ? "Processando…" : "Pagar"}
      </Button>
    </form>
  );
}

export interface CheckoutStripePageProps {
  orderId: string;
  clientSecret: string;
  publishableKey: string;
  onSuccess: (orderId: string) => void;
  onBack: () => void;
}

export function CheckoutStripePage({
  orderId,
  clientSecret,
  publishableKey,
  onSuccess,
  onBack,
}: CheckoutStripePageProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Coerce to string so Stripe Elements never receives an object.
  const safeOrderId = typeof orderId === "string" ? orderId : String(orderId ?? "");
  const safeClientSecret = typeof clientSecret === "string" ? clientSecret : String(clientSecret ?? "");
  const key = (typeof publishableKey === "string" ? publishableKey : String(publishableKey ?? "")) || (typeof import.meta !== "undefined" && import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY) || "";

  // All hooks must run on every render (React rules of hooks). No early return before useMemo.
  const stripePromise = getStripePromise(key);
  const options = useMemo(() => ({ clientSecret: safeClientSecret, appearance: { theme: "stripe" as const } }), [safeClientSecret]);

  if (!key) {
    return (
      <div className="min-h-screen bg-background pb-8">
        <header className="bg-primary border-b border-primary sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
            <button type="button" onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-xl text-white">Pagamento</h1>
          </div>
        </header>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <p className="text-muted-foreground">Pagamento não configurado. Configure a chave pública do Stripe.</p>
          <Button onClick={onBack} variant="outline" className="mt-4">
            Voltar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <header className="bg-primary border-b border-primary sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button type="button" onClick={onBack} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <div>
            <h1 className="text-xl text-white">Pagamento</h1>
            <p className="text-sm text-white/80">Complete com cartão, Pix ou boleto</p>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-card border border-border rounded-lg p-6 max-w-md mx-auto">
          {errorMessage && (
            <p className="text-sm text-destructive mb-4" role="alert">
              {safeText(errorMessage)}
            </p>
          )}
          <StripeFormErrorBoundary onBack={onBack}>
            <Elements stripe={stripePromise} options={options}>
              <CheckoutStripeForm
                orderId={safeOrderId}
                clientSecret={safeClientSecret}
                onSuccess={onSuccess}
                onError={(msg) => setErrorMessage(safeText(msg) || "")}
              />
            </Elements>
          </StripeFormErrorBoundary>
        </div>
      </div>
    </div>
  );
}
